import type { StepChannel, StepAdapter, WizardCommand } from '../types';
import type { WizardFormHandle } from '../../../types/blocks';

/**
 * Bridges a WizardFormHandle (form-engine's imperative API) to the channel protocol.
 * The adapter listens for commands from the wizard and emits signals from the form.
 *
 * Dirty tracking is fully event-driven: the FormBlockRenderer pushes dirty-state
 * changes through `handle.onDirtyChange`, which the adapter forwards as
 * `DIRTY_CHANGED` signals. No polling required.
 */
export function createFormAdapter(
  formHandle: WizardFormHandle,
  channel: StepChannel,
): StepAdapter {
  channel.emit({ type: 'READY' });
  channel.emit({ type: 'DIRTY_CHANGED', isDirty: formHandle.isDirty() });

  return {
    async handleCommand(cmd: WizardCommand) {
      switch (cmd.type) {
        case 'REQUEST_VALIDATE': {
          const valid = await formHandle.validate();
          if (valid) {
            const _vals = formHandle.getValues();
            channel.emit({
              type: 'VALIDATION_SUCCESS',
              values: _vals,
            });
          } else {
            channel.emit({
              type: 'VALIDATION_FAILURE',
              errors: {},
            });
          }
          break;
        }

        case 'REQUEST_SUBMIT': {
          channel.emit({ type: 'SUBMIT_STARTED' });
          try {
            const result = await formHandle.submit();
            channel.emit({
              type: 'SUBMIT_SUCCESS',
              result: (result as Record<string, unknown>) ?? {},
            });
          } catch (err) {
            channel.emit({ type: 'SUBMIT_FAILURE', error: err });
          }
          break;
        }

        case 'REQUEST_VALUES':
          channel.emit({
            type: 'VALUES_COLLECTED',
            values: formHandle.getValues(),
          });
          break;

        case 'REQUEST_RESET':
          formHandle.reset();
          break;

        case 'INJECT_DATA':
          formHandle.reset(cmd.data);
          break;
      }
    },

    destroy() {
      // no-op — cleanup handled by FormBlockRenderer ref teardown
    },
  };
}
